// src/calculators/seniority.js — трите панела на Фаза 3: „Трудов стаж",
// „Клас прослужено време" и „Работни дни". Нормативната сметка е в
// services/seniority.js — обща за тестове и екран (моделът на severance).

import { el, clear, field, input, row } from '../shared/dom.js';
import { money } from '../shared/format.js';
import { dateInput } from '../shared/dates.js';
import { toast } from '../shared/toast.js';
import {
  periodSeniority, sumSeniority, experienceBonus, workdaysReport,
  MIN_RATE_PER_YEAR,
} from '../../services/seniority.js';
import * as holidays from '../../services/holidays.js';
// Правното основание (задача 6/6) — цитатите са сверени в lex.bg.
import { withLaw, hintify } from './law.js';

const okIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');
const words = (s) => s.years + ' г. ' + s.months + ' м. ' + s.days + ' д.';

// Предаването „стаж → клас": бутонът в „Трудов стаж" оставя резултата тук,
// а панелът на класа го взима (еднократно) при построяване.
let _handoff = null;
function takeHandoff() { const h = _handoff; _handoff = null; return h; }

// ---- Трудов стаж (чл. 355 КТ; от 01.09.2026 — пропорционален ред, ДВ 69/2026) ----

export function buildSeniorityCalc() {
  const rows = [{ from: '', to: '', note: '' }];
  let lastSum = null;

  const perSel = el('select', {},
    el('option', { value: '30', text: '30 — календарни периоди от документи (трудова книжка)', selected: true }),
    el('option', { value: '21', text: '21 — изработени РАБОТНИ дни (чл. 9, ал. 4 от Наредбата)' }));

  const rowsWrap = el('div');
  const result = el('div', { class: 'calc-result' });

  function recompute() {
    clear(result);
    const parts = [];
    let calendarTotal = 0;
    let warn = '';
    for (const r of rows) {
      if (!okIso(r.from) || !okIso(r.to) || r.from > r.to) continue;
      const p = periodSeniority({ from: r.from, to: r.to, hoursPerDay: r.hours ? Number(r.hours) : undefined });
      if (p.warning) warn = p.warning;
      if (p.note) warn = warn || p.note; // новият пропорционален ред се обявява видимо
      parts.push(p);
      calendarTotal += Math.round((new Date(r.to + 'T00:00:00Z') - new Date(r.from + 'T00:00:00Z')) / 86400000) + 1;
      r._last = p;
    }
    lastSum = parts.length ? sumSeniority(parts, { daysPerMonth: Number(perSel.value) }) : null;
    if (warn) result.appendChild(el('div', { class: 'calc-hint-line', text: '⚠ ' + warn }));
    if (!lastSum) { result.appendChild(el('div', { class: 'calc-line', text: 'Попълнете поне един период (от – до).' })); return; }
    result.appendChild(el('div', { class: 'calc-line big' }, el('span', { text: 'Общ трудов стаж' }), el('span', { text: words(lastSum) })));
    result.appendChild(el('div', { class: 'calc-line' }, el('span', { text: 'Календарни дни по периодите (за сверка)' }), el('span', { text: String(calendarTotal) })));
  }

  function drawRows() {
    clear(rowsWrap);
    rows.forEach((r, i) => {
      const fromI = dateInput(r.from);
      fromI.addEventListener('input', () => { r.from = fromI.dataset.iso; recompute(); });
      const toI = dateInput(r.to);
      toI.addEventListener('input', () => { r.to = toI.dataset.iso; recompute(); });
      const noteI = input({ value: r.note || '', placeholder: 'напр. „Фирма X" (по избор)' });
      noteI.addEventListener('input', () => { r.note = noteI.value; });
      const hoursI = input({ type: 'number', step: '0.5', min: '0', max: '12', value: r.hours || '', placeholder: '8', style: 'max-width:70px' });
      hoursI.addEventListener('input', () => { r.hours = hoursI.value; recompute(); });
      rowsWrap.appendChild(el('div', { class: 'field-row', style: 'align-items:flex-end' },
        field('От дата', fromI, { size: 's' }),
        field('До дата (вкл.)', toI, { size: 's' }),
        hintify(field('Часове/ден', hoursI, { size: 's' }),
          'чл. 355, ал. 2 КТ (ред. ДВ 69/2026, от 01.09.2026)', 'Непълното работно време се зачита пропорционално; за периоди до 31.08.2026 — цял ден при поне половината от 8 часа.'),
        field('Бележка', noteI),
        el('button', { class: 'btn sm danger', type: 'button', 'aria-label': 'Махни периода', text: '×', style: 'margin-bottom:2px', onClick: () => { rows.splice(i, 1); if (!rows.length) rows.push({ from: '', to: '', note: '' }); drawRows(); recompute(); } })));
    });
    rowsWrap.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: '+ Добави период', onClick: () => { rows.push({ from: '', to: '', note: '' }); drawRows(); } }));
  }
  drawRows();
  perSel.addEventListener('change', recompute);
  recompute();

  return withLaw(el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Трудов стаж (чл. 355 КТ)' }),
    // (ДВ 69/2026, сверено в lex.bg) Новият ред е В СИЛА ОТ 01.09.2026 —
    // по-ранната бележка тук твърдеше 01.01.2027; законът печели.
    el('div', { class: 'calc-hint-line', text: '⚠ От 01.09.2026 г. непълното работно време се зачита ПРОПОРЦИОНАЛНО (чл. 355, ал. 2, ред. ДВ 69/2026). За периоди до 31.08.2026 г. важи старият ред: ден при поне 4 от 8 часа. Калкулаторът дели периода на границата сам.' }),
    el('p', { style: 'font-size:13px;color:var(--muted);margin:0 0 10px', text: 'Периодите се броят от дата до дата ВКЛЮЧИТЕЛНО, календарно — почивните и празничните дни са стаж (чл. 352 КТ). При непълно работно време попълнете „Часове/ден".' }),
    rowsWrap,
    field('Дни за 1 месец при сумирането', perSel),
    result,
    el('button', { class: 'btn', type: 'button', text: 'Ползвай като признат стаж за клас', style: 'margin-top:10px', onClick: () => {
      if (!lastSum) { toast('Първо попълнете периодите.', true); return; }
      _handoff = lastSum;
      // Мостът window.openCalc е публикуван от регистъра — прекият import
      // би направил кръг между файловете.
      window.openCalc('experience-bonus');
    } })), {
    when: [
      'Ново назначение: събирате стажа от трудовата книжка/УП-3 по периоди, за да определите класа.',
      'Периоди при няколко работодатели се сумират — 30 дни стават месец, 12 месеца — година.',
      'Служител на непълно работно време: до 31.08.2026 г. ден се брои цял при поне половината; от 01.09.2026 г. — пропорционално.',
      'Резултатът се прехвърля с един бутон към „Клас прослужено време".',
    ],
    quotes: [
      { ref: 'чл. 351, ал. 1 КТ', text: 'Трудов стаж по смисъла на този кодекс е времето, през което работникът или служителят е работил по трудово правоотношение, доколкото друго не е предвидено в този кодекс или в друг закон, както и времето, през което лицето е работило като държавен служител.' },
      { ref: 'чл. 355, ал. 1 и 2 КТ (изм. ДВ, бр. 69 от 2026 г., в сила от 01.09.2026)', text: 'Трудовият стаж се изчислява в часове, дни, месеци и години. За един ден трудов стаж се признава времето, през което работникът или служителят е изработил пълното законоустановено за него работно време за деня. Когато работникът или служителят работи при непълно работно време, трудовият стаж се изчислява пропорционално на законоустановеното му работно време.' },
      { ref: 'чл. 9, ал. 1 и 4 от Наредбата за трудовия стаж (загл. изм. ДВ, бр. 65/2024)', text: 'Трудовият стаж се изчислява в дни, месеци и години. … При изработени през отделни месеци на годината по-малко от работните дни трудовият стаж се признава за толкова месеца, колкото се получават, като общият брой на изработените дни през тези месеци се раздели на 21.' },
    ],
  });
}

// ---- Клас прослужено време (НСОРЗ чл. 12) -----------------------------------

export function buildExperienceBonusCalc() {
  const pre = takeHandoff();
  const f = {
    base: input({ type: 'number', step: '0.01', min: '0', value: '', placeholder: 'напр. 1000' }),
    years: input({ type: 'number', min: '0', value: pre ? pre.years : '', placeholder: '0', style: 'max-width:80px' }),
    months: input({ type: 'number', min: '0', max: '11', value: pre ? pre.months : '', placeholder: '0', style: 'max-width:80px' }),
    days: input({ type: 'number', min: '0', max: '30', value: pre ? pre.days : '', placeholder: '0', style: 'max-width:80px' }),
    rate: input({ type: 'number', step: '0.1', min: String(MIN_RATE_PER_YEAR), value: String(MIN_RATE_PER_YEAR) }),
  };
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });

  function recompute() {
    clear(result); clear(notesBox);
    const base = Number(f.base.value);
    if (!(base > 0)) { result.appendChild(el('div', { class: 'calc-line', text: 'Въведете основна заплата.' })); return; }
    let r;
    try {
      r = experienceBonus({
        baseSalary: base,
        seniority: { years: Number(f.years.value) || 0, months: Number(f.months.value) || 0, days: Number(f.days.value) || 0 },
        ratePerYear: f.rate.value === '' ? undefined : Number(f.rate.value),
      });
    } catch (e) {
      result.appendChild(el('div', { class: 'calc-hint-line', text: '⚠ ' + e.message }));
      return;
    }
    result.appendChild(el('div', { class: 'calc-line' }, el('span', { text: 'Пълни години (ал. 10)' }), el('span', { text: String(r.fullYears) })));
    result.appendChild(el('div', { class: 'calc-line' }, el('span', { text: 'Процент клас' }), el('span', { text: String(r.percent).replace('.', ',') + ' %' })));
    result.appendChild(el('div', { class: 'calc-line big' }, el('span', { text: 'Сума на месец' }), el('span', { text: money(r.monthlyAmount, 'EUR') })));
    r.notes.forEach((n) => notesBox.appendChild(el('div', { style: 'font-size:12px;color:var(--muted);padding:2px 0', text: '• ' + n })));
  }
  Object.values(f).forEach((i) => i.addEventListener('input', recompute));
  recompute();
  if (pre) toast('Стажът ' + words(pre) + ' е пренесен от калкулатора „Трудов стаж".');

  return withLaw(el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Клас прослужено време (НСОРЗ чл. 12)' }),
    hintify(field('Основна заплата по договора (не брутната!)', f.base),
      'НСОРЗ чл. 12, ал. 1', 'Класът е процент върху ОСНОВНАТА работна заплата по индивидуалния трудов договор, не върху брутната.'),
    field('Признат стаж (години / месеци / дни)',
      el('div', { style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap' },
        f.years, el('span', { text: 'г.' }), f.months, el('span', { text: 'м.' }), f.days, el('span', { text: 'д.' }))),
    hintify(field('Ставка за година (%) — минимум 0,6 (ПМС № 147/2007), повече по КТД', f.rate),
      'ПМС № 147/2007, чл. единствен', 'Минималният размер е 0,6 на сто за всяка година придобит трудов стаж и професионален опит.'),
    result,
    notesBox), {
    when: [
      'Назначавате служител с предишен стаж на сходна длъжност — определяте процента клас към заплатата.',
      'Годишнина от постъпването: класът се увеличава при всяка навършена пълна година (ал. 10).',
      'Проверка на фиш: класът се смята върху основната заплата, не върху брутото.',
      'Класът е добавка с постоянен характер — влиза в базата за обезщетения (НСОРЗ чл. 15, ал. 1).',
    ],
    quotes: [
      { ref: 'НСОРЗ чл. 12, ал. 1', text: 'За придобит трудов стаж и професионален опит на работниците и служителите се заплаща допълнително месечно възнаграждение в процент върху основната работна заплата, определена с индивидуалния трудов договор.' },
      { ref: 'НСОРЗ чл. 12, ал. 9 и 10 (нови — ДВ, бр. 56/2007)', text: 'Правото за получаване на допълнителното трудово възнаграждение за трудов стаж и професионален опит възниква при придобит трудов стаж и професионален опит не по-малък от една година. Размерът… се изменя на период, не по-малък от една година придобит трудов стаж и професионален опит.' },
      { ref: 'ПМС № 147 от 29.06.2007 г., чл. единствен', text: 'Определя минимален размер на допълнителното трудово възнаграждение за придобит трудов стаж и професионален опит 0,6 на сто за всяка година придобит трудов стаж и професионален опит.' },
      { ref: 'НСОРЗ чл. 15, ал. 1', text: 'Допълнителните трудови възнаграждения по чл. 11 и 12 са с постоянен характер.' },
    ],
  });
}

// ---- Работни дни между дати (изцяло върху services/holidays.js) -------------

export function buildWorkdaysCalc() {
  const fromI = dateInput('');
  const toI = dateInput('');
  const nextI = dateInput('');
  const result = el('div', { class: 'calc-result' });
  const holidaysBox = el('div', { style: 'margin-top:8px' });
  const nextOut = el('div', { class: 'calc-line big' });

  function recompute() {
    clear(result); clear(holidaysBox);
    const from = fromI.dataset.iso, to = toI.dataset.iso;
    if (!okIso(from) || !okIso(to) || from > to) {
      result.appendChild(el('div', { class: 'calc-line', text: 'Попълнете период (от – до).' }));
      return;
    }
    const r = workdaysReport({ from, to });
    result.appendChild(el('div', { class: 'calc-line' }, el('span', { text: 'Календарни дни (вкл. двете дати)' }), el('span', { text: String(r.calendarDays) })));
    result.appendChild(el('div', { class: 'calc-line big' }, el('span', { text: 'Работни дни' }), el('span', { text: String(r.workingDays) })));
    result.appendChild(el('div', { class: 'calc-line' }, el('span', { text: 'Почивни и празнични' }), el('span', { text: String(r.nonWorkingDays) })));
    if (r.holidays.length) {
      holidaysBox.appendChild(el('div', { style: 'font-weight:600;margin:6px 0 2px', text: 'Официални празници в периода:' }));
      r.holidays.forEach((h) => holidaysBox.appendChild(el('div', { style: 'font-size:13px;padding:1px 0', text: h.date.split('-').reverse().join('.') + ' — ' + h.name })));
    }
  }
  function recomputeNext() {
    clear(nextOut);
    const d = nextI.dataset.iso;
    if (!okIso(d)) return;
    const nd = holidays.nextWorkingDay(d);
    nextOut.appendChild(el('span', { text: 'Следващ работен ден' }));
    nextOut.appendChild(el('span', { text: nd.split('-').reverse().join('.') }));
  }
  fromI.addEventListener('input', recompute);
  toI.addEventListener('input', recompute);
  nextI.addEventListener('input', recomputeNext);
  recompute();

  return withLaw(el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Работни дни между дати' }),
    row(hintify(field('От дата', fromI, { size: 's' }),
      'чл. 154, ал. 2 КТ (нова — ДВ, бр. 105/2016)', 'Празник (без Великденските), паднал в събота/неделя, прави първия или първите два работни дни след него неприсъствени.'),
      field('До дата (вкл.)', toI, { size: 's' })),
    result,
    holidaysBox,
    el('div', { style: 'border-top:1px solid var(--border);margin-top:12px;padding-top:10px' },
      field('Следващ работен ден след', nextI, { size: 's' }),
      nextOut),
    el('p', { style: 'font-size:12px;color:var(--muted);margin-top:10px', text: 'Разместените от МС дни се отчитат само ако са въведени в календарните изключения на фирмата — този панел работи с общия календар (вкл. автоматичното пренасяне по чл. 154, ал. 2).' })), {
    when: [
      'Смятате срок „до края на месеца" или „в 7-дневен срок" и трябва да знаете работните дни.',
      'Падеж или срок за подаване пада в почивен ден — търсите следващия работен.',
      'Планиране на заплати/аванси: колко работни дни има месецът.',
      'Проверка кои официални празници попадат в период и къде се пренасят по чл. 154, ал. 2.',
    ],
    quotes: [
      { ref: 'чл. 154, ал. 1 КТ (изм. ДВ, бр. 107/2020)', text: 'Официални празници са: 1 януари – Нова година; 3 март – Ден на Освобождението на България от османско иго – национален празник; 1 май; 6 май; 24 май; 6 септември; 22 септември; 24 декември, 25 и 26 декември; Велики петък, Велика събота и Великден…' },
      { ref: 'чл. 154, ал. 2 КТ (нова — ДВ, бр. 105/2016)', text: 'Когато официалните празници по ал. 1, с изключение на Великденските празници, съвпадат със събота и/или неделя, първият или първите два работни дни след тях са неприсъствени.' },
    ],
  });
}
